import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import Map, { Marker, MapRef } from 'react-map-gl/mapbox';
import { X } from 'lucide-react';
import { useGetFeedQuery, PostResponseSchema } from '@api/client';
import { applyMapStyling, MAPBOX_TOKEN } from '../../share/utils/mapStyling';
import { MapMarker } from '@feature/map/components/markers/MapMarker';
import PostCard from '@feature/post-item/PostCard';
import IntroAnimation from './IntroAnimation';
import { MAP_ZOOM_LEVELS } from '../constants/mapConstants';
import { VideoPlayerModal } from '@feature/play-video';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../../../App.css';

interface HeroGlobeProps {
  isMobile: boolean;
  onZoomChange?: (zoom: number) => void;
  onPostSelect?: (selected: boolean) => void;
  onGlobeLayout?: (leftPosition: number, rightPosition: number, topPosition: number, bottomPosition: number) => void;
  isPhoneFrameMode?: boolean;
  onIntroComplete?: () => void;
}

const SECONDS_PER_REVOLUTION = 60;
const AUTO_DEMO_INACTIVITY_MS = 10_000;
const AUTO_DEMO_VIDEO_DURATION_MS = 10_000;
const AUTO_DEMO_PHOTO_DURATION_MS = 4_000;
const AUTO_DEMO_PAUSE_BEFORE_NEXT_MS = 3_000;

export default function HeroGlobe({ isMobile, onZoomChange, onPostSelect, onGlobeLayout, isPhoneFrameMode = false, onIntroComplete }: HeroGlobeProps): React.ReactElement {
  const mapRef = useRef<MapRef>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const userInteractingRef = useRef(false);
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const autoDemoTimersRef = useRef<NodeJS.Timeout[]>([]);
  const autoDemoActiveRef = useRef(false);
  const autoDemoLastPostIdRef = useRef<number | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isRotating, setIsRotating] = useState(true);
  const [selectedPost, setSelectedPost] = useState<PostResponseSchema | null>(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);

  const initialViewState = useMemo(() => {
    const isInitiallyMobile = window.innerWidth <= 1350;
    return {
      longitude: 0,
      latitude: 20,
      zoom: isInitiallyMobile ? MAP_ZOOM_LEVELS.INITIAL_MOBILE : MAP_ZOOM_LEVELS.INITIAL_DESKTOP,
      padding: { top: isInitiallyMobile ? 0 : 15, bottom: 0, left: 0, right: 0 },
    };
  }, []);

  // Disable body scroll when image modal is open — only for non-phone-frame (fullscreen) mode
  useEffect(() => {
    if (!isPhoneFrameMode && isImageModalOpen) {
      document.body.style.overflow = 'hidden';
    } else if (!isPhoneFrameMode) {
      document.body.style.overflow = '';
    }

    return () => {
      if (!isPhoneFrameMode) document.body.style.overflow = '';
    };
  }, [isImageModalOpen, isPhoneFrameMode]);

  // Fetch first 2 pages of popular feed posts
  const { data: feedDataPage1, isLoading: isLoadingPage1 } = useGetFeedQuery({
    layerId: 'popular',
    sortBy: 'trending',
    page: 1,
    size: 100,
  });

  const { data: feedDataPage2, isLoading: isLoadingPage2 } = useGetFeedQuery({
    layerId: 'popular',
    sortBy: 'trending',
    page: 2,
    size: 100,
  });

  // Combine both pages
  const feedData = useMemo(() => {
    if (!feedDataPage1 && !feedDataPage2) return null;

    const items = [
      ...(feedDataPage1?.items || []),
      ...(feedDataPage2?.items || [])
    ];

    return {
      items,
      total: feedDataPage1?.total || 0,
      page: 1,
      size: items.length,
      pages: feedDataPage1?.pages || 0
    };
  }, [feedDataPage1, feedDataPage2]);

  const isLoading = isLoadingPage1 || isLoadingPage2;

  // Spin globe function - event-driven rotation
  const spinGlobe = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map || !isRotating || userInteractingRef.current) return;

    const center = map.getCenter();
    const degreesPerSecond = 360 / SECONDS_PER_REVOLUTION;
    center.lng -= degreesPerSecond;

    // Smoothly animate over 1 second. When complete, 'moveend' event fires
    map.easeTo({
      center,
      duration: 1000,
      easing: (n) => n
    });
  }, [isRotating]);

  // Handle map load - apply styling and signal ready
  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    applyMapStyling(map);

    setIsMapLoaded(true);
  }, []);

  // Calculate text positions based on phone frame position
  useEffect(() => {
    if (isMobile || !onGlobeLayout || !mapContainerRef.current) return;

    const recalculateLayout = () => {
      if (!mapContainerRef.current?.parentElement) return;

      // Get the phone frame container (parent of globe container)
      const phoneFrameContainer = mapContainerRef.current.parentElement;
      const rect = phoneFrameContainer.getBoundingClientRect();
      const phoneFrameLeftEdge = rect.left;
      const phoneFrameRightEdge = rect.right;
      const viewportWidth = window.innerWidth;

      // Calculate center of space between viewport left edge and phone frame left edge
      const leftTextCenterpoint = phoneFrameLeftEdge / 2;

      // Calculate center of space between phone frame right edge and viewport right edge
      const spaceRight = viewportWidth - phoneFrameRightEdge;
      const rightTextCenterpoint = spaceRight / 2;

      // Not used in phone frame mode, but required by callback
      const topTextCenterpoint = 0;
      const bottomTextCenterpoint = 0;


      onGlobeLayout(leftTextCenterpoint, rightTextCenterpoint, topTextCenterpoint, bottomTextCenterpoint);
    };

    // Listen to resize events
    window.addEventListener('resize', recalculateLayout);

    // Calculate on mount
    recalculateLayout();

    return () => {
      window.removeEventListener('resize', recalculateLayout);
    };
  }, [isMobile, onGlobeLayout, isPhoneFrameMode]);

  // ── Idle timer: single source of truth ──
  // Any user interaction or open modal pauses the timer.
  // When the modal closes or interaction ends at globe level, the timer restarts.
  // When it fires, the auto-demo loop begins.

  const pauseIdleTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = undefined;
    }
  }, []);

  // scheduleIdleTimer and onUserInteraction are defined after auto-demo helpers
  // so they can reference startAutoDemoLoop. We use refs to break the dependency cycle.
  const onUserInteractionRef = useRef<() => void>(() => {});

  // Setup event listeners and start rotation
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !isMapLoaded) return;

    const handleInteractionStart = () => {
      userInteractingRef.current = true;
      onUserInteractionRef.current();
    };

    const handleInteractionEnd = () => {
      const currentMap = mapRef.current?.getMap();
      if (currentMap && onZoomChange) {
        onZoomChange(currentMap.getZoom());
      }
      if (currentMap && currentMap.getZoom() <= 2) {
        userInteractingRef.current = false;
        spinGlobe();
      }
    };

    map.on('mousedown', handleInteractionStart);
    map.on('touchstart', handleInteractionStart);
    map.on('wheel', handleInteractionStart);
    map.on('dblclick', handleInteractionStart);
    map.on('mouseup', handleInteractionEnd);
    map.on('touchend', handleInteractionEnd);
    map.on('dragend', handleInteractionEnd);
    map.on('pitchend', handleInteractionEnd);
    map.on('rotateend', handleInteractionEnd);
    map.on('zoomend', handleInteractionEnd);
    map.on('moveend', spinGlobe);

    spinGlobe();

    return () => {
      map.off('mousedown', handleInteractionStart);
      map.off('touchstart', handleInteractionStart);
      map.off('wheel', handleInteractionStart);
      map.off('dblclick', handleInteractionStart);
      map.off('mouseup', handleInteractionEnd);
      map.off('touchend', handleInteractionEnd);
      map.off('dragend', handleInteractionEnd);
      map.off('pitchend', handleInteractionEnd);
      map.off('rotateend', handleInteractionEnd);
      map.off('zoomend', handleInteractionEnd);
      map.off('moveend', spinGlobe);
    };
  }, [isMapLoaded, spinGlobe]);

  // Auto-demo helpers
  const clearAutoDemoTimers = useCallback(() => {
    autoDemoTimersRef.current.forEach(clearTimeout);
    autoDemoTimersRef.current = [];
  }, []);

  const addDemoTimer = useCallback((fn: () => void, ms: number) => {
    const id = setTimeout(fn, ms);
    autoDemoTimersRef.current.push(id);
    return id;
  }, []);

  const stopAutoDemo = useCallback(() => {
    autoDemoActiveRef.current = false;
    clearAutoDemoTimers();
  }, [clearAutoDemoTimers]);

  const zoomOutToGlobe = useCallback((map: mapboxgl.Map) => {
    const isActuallyMobile = window.innerWidth <= 1350;
    map.flyTo({
      center: [map.getCenter().lng, 20],
      zoom: isActuallyMobile ? MAP_ZOOM_LEVELS.INITIAL_MOBILE : MAP_ZOOM_LEVELS.INITIAL_DESKTOP,
      pitch: 0,
      duration: 2500,
      padding: { top: isActuallyMobile ? 0 : 15, bottom: 0, left: 0, right: 0 },
      essential: true,
    });
  }, []);

  const startAutoDemoLoop = useCallback(() => {
    if (!feedData?.items.length) return;
    autoDemoActiveRef.current = true;

    const mediaPosts = feedData.items.filter((p) => p.m3u8_url || p.photo_url);
    if (mediaPosts.length === 0) return;

    const pickRandomPost = (): PostResponseSchema => {
      const candidates = mediaPosts.length > 1
        ? mediaPosts.filter((p) => p.id !== autoDemoLastPostIdRef.current)
        : mediaPosts;
      return candidates[Math.floor(Math.random() * candidates.length)];
    };

    const showNextPost = () => {
      if (!autoDemoActiveRef.current) return;
      const map = mapRef.current?.getMap();
      if (!map) return;

      const post = pickRandomPost();
      autoDemoLastPostIdRef.current = post.id;

      userInteractingRef.current = true;
      if (onPostSelect) onPostSelect(true);

      map.flyTo({
        center: [post.longitude, post.latitude],
        zoom: MAP_ZOOM_LEVELS.STREET_LEVEL,
        duration: 3000,
        padding: { top: isMobile ? 0 : 15, bottom: 0, left: 0, right: 0 },
        essential: true,
      });

      addDemoTimer(() => {
        if (!autoDemoActiveRef.current) return;
        setSelectedPost(post);

        addDemoTimer(() => {
          if (!autoDemoActiveRef.current) return;
          if (post.m3u8_url) {
            setIsVideoModalOpen(true);
          } else if (post.photo_url) {
            setIsImageModalOpen(true);
          }

          const viewDuration = post.m3u8_url ? AUTO_DEMO_VIDEO_DURATION_MS : AUTO_DEMO_PHOTO_DURATION_MS;
          addDemoTimer(() => {
            if (!autoDemoActiveRef.current) return;
            setIsVideoModalOpen(false);
            setIsImageModalOpen(false);
            document.body.style.overflow = '';
            setSelectedPost(null);
            if (onPostSelect) onPostSelect(false);

            const currentMap = mapRef.current?.getMap();
            if (currentMap) zoomOutToGlobe(currentMap);

            addDemoTimer(() => {
              if (!autoDemoActiveRef.current) return;
              userInteractingRef.current = false;
              spinGlobe();

              addDemoTimer(() => {
                if (!autoDemoActiveRef.current) return;
                showNextPost();
              }, AUTO_DEMO_PAUSE_BEFORE_NEXT_MS);
            }, 2700);
          }, viewDuration);
        }, 800);
      }, 3200);
    };

    showNextPost();
  }, [feedData, isMobile, onPostSelect, spinGlobe, addDemoTimer, zoomOutToGlobe]);

  const scheduleIdleTimer = useCallback(() => {
    pauseIdleTimer();
    inactivityTimeoutRef.current = setTimeout(() => {
      startAutoDemoLoop();
    }, AUTO_DEMO_INACTIVITY_MS);
  }, [pauseIdleTimer, startAutoDemoLoop]);

  // Central handler: any user interaction cancels auto-demo, closes modals, restarts idle timer
  const onUserInteraction = useCallback(() => {
    stopAutoDemo();
    setIsVideoModalOpen(false);
    setIsImageModalOpen(false);
    document.body.style.overflow = '';
    setSelectedPost(null);
    if (onPostSelect) onPostSelect(false);
    scheduleIdleTimer();
  }, [stopAutoDemo, scheduleIdleTimer, onPostSelect]);
  onUserInteractionRef.current = onUserInteraction;

  // Start idle timer once intro completes and feed is loaded
  useEffect(() => {
    if (introComplete && feedData?.items.length && isMapLoaded) {
      scheduleIdleTimer();
    }
    return () => {
      pauseIdleTimer();
      clearAutoDemoTimers();
    };
  }, [introComplete, feedData, isMapLoaded]);

  // Close modal - zoom out immediately to initial state, restart idle timer
  const closeModal = useCallback(() => {
    pauseIdleTimer();
    if (onPostSelect) onPostSelect(false);
    setSelectedPost(null);

    const map = mapRef.current?.getMap();
    if (map) {
      zoomOutToGlobe(map);
      setTimeout(() => {
        userInteractingRef.current = false;
        spinGlobe();
        scheduleIdleTimer();
      }, 2700);
    }
  }, [spinGlobe, onPostSelect, zoomOutToGlobe, pauseIdleTimer, scheduleIdleTimer]);

  // Handle marker click - cancel auto-demo, pause idle timer, show post
  const handleMarkerClick = useCallback((post: PostResponseSchema) => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    stopAutoDemo();
    pauseIdleTimer();
    userInteractingRef.current = true;
    if (onPostSelect) onPostSelect(true);

    // Zoom in to the marker at street level
    map.easeTo({
      center: [post.longitude, post.latitude],
      zoom: MAP_ZOOM_LEVELS.STREET_LEVEL,
      duration: 1000,
      padding: { top: isMobile ? 0 : 15, bottom: 0, left: 0, right: 0 }
    });

    // Show post modal (timer will NOT start while modal is open)
    setSelectedPost(post);
  }, [onPostSelect]);

  return (
    <>
      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: 'transparent',
      }}>
        {/* Intro Animation */}
        {!introComplete && (
          <IntroAnimation
            onComplete={() => {
              setIntroComplete(true);
              if (onIntroComplete) {
                onIntroComplete();
              }
            }}
            isMobile={isMobile}
          />
        )}

        <div
          ref={mapContainerRef}
          style={{
            width: '100%',
            height: '100%',
            background: 'transparent',
            opacity: isLoading || !introComplete ? 0 : 1,
            transition: 'opacity 0.2s ease',
          }}
        >
          <Map
            ref={mapRef}
            initialViewState={initialViewState}
            onLoad={handleMapLoad}
            mapboxAccessToken={MAPBOX_TOKEN}
            mapStyle="mapbox://styles/mapbox/outdoors-v12"
            style={{ width: '100%', height: '100%', background: 'transparent' }}
            projection={{ name: 'globe' }}
            attributionControl={false}
            renderWorldCopies={false}
            dragPan={true}
            scrollZoom={true}
            doubleClickZoom={true}
            touchZoomRotate={true}
            boxZoom={false}
          >
            {feedData?.items.map((post) => (
              <Marker
                key={post.id}
                longitude={post.longitude}
                latitude={post.latitude}
                anchor="bottom"
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMarkerClick(post);
                  }}
                  style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.2)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <MapMarker post={post} />
                </div>
              </Marker>
            ))}
          </Map>
        </div>

        {/* Loading spinner */}
        {isLoading && introComplete && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e2e8f0',
              borderTop: '4px solid #dc2626',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
          </div>
        )}
      </div>

      <style>
        {`
          .mapboxgl-canvas {
            background: transparent !important;
          }
          .mapboxgl-map {
            background: transparent !important;
          }
          .mapboxgl-ctrl-logo {
            display: none !important;
          }
          .mapboxgl-ctrl-attrib {
            display: none !important;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Post Preview Modal */}
      {selectedPost && !isVideoModalOpen && !isImageModalOpen && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'transparent',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
            paddingBottom: '60px',
          }}
          onClick={closeModal}
        >
          <div
            style={{
              borderRadius: '12px',
              maxWidth: isMobile ? '90%' : '400px',
              width: '100%',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <PostCard
              post={selectedPost}
              onVideoClick={(post) => {
                setIsVideoModalOpen(true);
              }}
              onImageClick={(post) => {
                setIsImageModalOpen(true);
              }}
              roundedBottom={true}
              compact={true}
              showCloseButton={true}
              onClose={closeModal}
            />
          </div>
        </div>
      )}

      {/* Video Modal */}
      <VideoPlayerModal
        isOpen={isVideoModalOpen && Boolean(selectedPost?.m3u8_url)}
        videoUrl={selectedPost?.m3u8_url || ''}
        onClose={() => {
          setIsVideoModalOpen(false);
          closeModal();
        }}
        playing
        controls
        loop
        muted={autoDemoActiveRef.current}
        isPhoneFrameMode={isPhoneFrameMode}
      />

      {/* Image Modal */}
      {isImageModalOpen && selectedPost?.photo_url && (
        <div
          className="image-modal"
          style={isPhoneFrameMode ? {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: '100%',
            borderRadius: '45px',
            overflow: 'hidden',
          } : undefined}
          onClick={() => {
            setIsImageModalOpen(false);
            closeModal();
          }}
        >
          <button
            style={{
              position: isPhoneFrameMode ? 'absolute' : 'fixed',
              top: isPhoneFrameMode ? '50px' : '80px',
              right: isPhoneFrameMode ? '20px' : '20px',
              background: 'rgba(0, 0, 0, 0.6)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsImageModalOpen(false);
              closeModal();
            }}
          >
            <X size={20} />
          </button>
          <img
            src={selectedPost.photo_url}
            alt="Post content"
            className="image-modal-content"
            style={isPhoneFrameMode ? {
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            } : undefined}
          />
        </div>
      )}
    </>
  );
}
